import React from "react";
import {
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IB2BSession } from "src/Types/module/b2bSession/b2bSession.types";
import dayjs from "dayjs";

interface Props {
	label?: string;
	value: number | string;
	onChange: (sessionId: number) => void;
	error?: boolean;
	helperText?: string;
	required?: boolean;
}

const formatDisplayTime = (timeStr?: string) => {
	if (!timeStr) return "";
	const [hours, minutes] = timeStr.split(":");
	if (hours === undefined || minutes === undefined) return timeStr;
	const dummyDate = dayjs().hour(Number(hours)).minute(Number(minutes));
	return dummyDate.format("hh:mm A");
};

const SessionSelectField: React.FC<Props> = ({
	label = "Select Session",
	value,
	onChange,
	error,
	helperText,
	required,
}) => {
	const { data: rawSessions = [], isLoading } = useApiQuery<IB2BSession[]>({
		endPoint: "/api/fair/admin/sessions",
		queryKeys: ["B2BSessions"],
	});

	const sessions = Array.isArray(rawSessions) ? rawSessions : [];

	return (
		<FormControl fullWidth size="small" error={error} required={required}>
			<InputLabel id="session-select-label" shrink>
				{label}
			</InputLabel>
			<Select
				labelId="session-select-label"
				value={value || ""}
				label={label}
				notched
				onChange={(e) => onChange(Number(e.target.value))}
				displayEmpty
			>
				<MenuItem value="" disabled>
					<em>{isLoading ? "Loading sessions..." : "Choose a session"}</em>
				</MenuItem>
				{sessions.map((s) => {
					const dateFormatted = s.date
						? dayjs(s.date).format("DD MMM, YYYY")
						: "";
					const timeSlot = `${formatDisplayTime(s.start_time)} – ${formatDisplayTime(
						s.end_time
					)}`;
					return (
						<MenuItem key={s.id} value={s.id}>
							{s.session_no || `Session ${s.id}`} &nbsp;&bull;&nbsp; {dateFormatted} ({timeSlot})
						</MenuItem>
					);
				})}
			</Select>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
		</FormControl>
	);
};

export default SessionSelectField;
